import React from 'react';
import DocumentPage, {
  Section,
  Paragraph,
  DoDoNot,
  HazardTable,
} from './DocumentPage';

const GuillotineDoc = ({ onReturnHome }) => (
  <DocumentPage
    pageTitle="Metal Cutting Guillotine – RA & SSOW"
    documentHeading="Risk Assessment 07 & Safe System of Work: Metal Working Guillotines (Manual and Powered)"
    meta={[
      ['Document Reference', 'PVS-RAMS-GUILL-001'],
      ['Version', '1.0'],
      ['Date of Issue', '17th March 2026'],
      ['Next Review Date', '17th March 2028'],
      ['Area / Location', 'Workshop'],
      ['Activity', 'Use of electric / powered metal cutting guillotine'],
      ['Review Period', '24 months'],
    ]}
    acknowledgment={{
      documentName: 'Metal Cutting Guillotine – RA & SSOW',
      documentReference: 'PVS-RAMS-GUILL-001',
      processActivity: 'Use of Metal Cutting Guillotine',
      versionNumber: '1.0',
      dateOfIssue: '17th March 2026',
    }}
    onReturnHome={onReturnHome}
  >
    <Section number="1" title="Introduction">
      <Paragraph>
        Metalworking guillotines typically use a clamp securing the sheet of material, then with a
        scissor action, the blade shears the material, starting at one side of the sheet. There are
        a number of hazards associated with these machines including cuts from handling materials,
        entanglement in the machine, eye injuries from flying debris, electric shocks from faulty
        equipment, burns from hot sheets of metal, trips / falls / slips, and manual handling
        injuries.
      </Paragraph>
    </Section>

    <Section number="2" title="Hazard Identification and Risk Assessment">
      <HazardTable
        columns={['Hazard', 'Control Measures Already in Place', 'Additional Control Measures Required']}
        rows={[
          [
            'Crushing injuries / amputation of fingers or hands.',
            [
              'Only fully trained operatives are allowed to use the equipment.',
              "Under 18s are not allowed to operate this machine except as part of a strictly supervised training programme.",
              'Staff are instructed not to distract the operator while the machine is in use.',
              'The machine is not left unattended while running.',
              'The machine and its controls are suitably marked.',
              'PPE signage is displayed on/or near the machine.',
              'Lone operating of this machine is strictly prohibited.',
              'The start / stop controls are clearly identified.',
              "The guillotine's foot pedal is shrouded.",
              'The emergency stop is clearly marked and accessible.',
              'The side rails are caged to prevent entry.',
              'The side gate has a bolt in place and is fitted with an interlock device.',
              'A mesh guard is fitted to the rail to ensure access is prohibited within the cut zone.',
              'Operators are instructed that under no circumstances should persons enter the area at the back of the machine.',
              'There are regular inspections of equipment and safety devices such as interlock switches by competent persons.',
            ],
            'None required.',
          ],
          [
            'Crushing (cut metal falling at rear of machine).',
            [
              'A secure zone is ensured with the use of rails and gate to ensure access to the rear of the machine controlled.',
              'Operatives are instructed not to access the rear of the machine when in use.',
            ],
            'None required.',
          ],
          [
            'Ejection of material being worked upon.',
            [
              'The machine is fitted with a clamp bar to prevent the work piece moving during the cutting cycle.',
              'The guillotine is not used to cut material thicker than the maximum rating specified by the manufacturer.',
            ],
            'None required.',
          ],
          [
            'Cuts from sharp metal being worked on.',
            [
              'Appropriate PPE in the form of safety footwear and heavy duty gloves are provided and worn.',
              'Operatives are instructed that when handling large sharp objects team assistance should be sought.',
            ],
            'None required.',
          ],
          [
            'Noise induced hearing loss.',
            ['Appropriate PPE in the form of hearing protection is provided and worn.'],
            'None required.',
          ],
          [
            'Slips, trips and falls.',
            [
              'Good standards of housekeeping are observed and maintained.',
              "The guillotine's foot pedal is shrouded.",
              'Appropriate lighting is provided.',
            ],
            'None required.',
          ],
        ]}
      />
    </Section>

    <Section number="3" title="Safe System of Work">
      <DoDoNot
        doItems={[
          'Locate and ensure you are familiar with all machine operations and controls.',
          'Ensure all guards are fitted, secure and functional. Do not operate if guards are missing or faulty.',
          'Check workspaces and walkways to ensure no slip/trip hazards are present.',
          'Ensure working parts are well lubricated and free of rust and dirt.',
          'Ensure the area around the machine is adequately lit.',
          'Be aware of other people in the area. Ensure the area is clear before using equipment.',
          'Ensure cutting table is clear of scrap and tools.',
          'Ensure you are wearing all required PPE for the job and in accordance with any training and instructions given, e.g. gloves for handling material with sharp edges, hearing protection, safety footwear, eye protection etc.',
          'Only one person may operate this machine at any one time.',
          'Use correct lifting procedures when handling large sheets of material.',
          'Take care during the initial feeding of the workpiece into the machine.',
          'The workpiece should always be held sufficiently far back from the edge being fed into the guillotine.',
          'Ensure fingers and limbs are clear before operating the guillotine.',
          'Ensure material is firmly held to prevent inaccurate cutting due to creep.',
          'When cutting, ensure your feet are positioned to avoid unintentional contact with the foot lever.',
          'Place all off-cuts in the storage rack or waste bin.',
          'Leave the work area in a safe, clean, and tidy state.',
        ]}
        doNotItems={[
          'Operate a machine with known defects.',
          'Operate the machine with loose clothing, neck-ties, rings, other jewellery and long hair (unless tied back or covered) to prevent entanglement.',
          'Lean against the machine while it is in operation.',
          "Use the machine to cut materials outside of the manufacturer's recommended thickness specification.",
          'Remove material from the rear of the machine unless the machine has been isolated.',
          'Clean the machine, conduct any maintenance or adjustments unless the machine has been isolated at the mains isolation switch.',
          'Approach the rear of the machine during the cutting operation.',
          'Clean the machine with compressed air.',
          'Use the machine to cut materials that are outside the design capabilities e.g. rod, strap or wire.',
        ]}
      />
    </Section>

    <Section number="4" title="Reminder">
      <Paragraph className="font-semibold">
        IF YOU HAVE ANY QUESTIONS, PLEASE ASK YOUR MANAGER OR SUPERVISOR. You have a duty not only
        to protect your own health and safety but also to ensure that your actions do not put others
        around you at risk.
      </Paragraph>
    </Section>
  </DocumentPage>
);

export default GuillotineDoc;
